import React, { useState, useCallback, useEffect } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import {
  Searchbar,
  Chip,
  Button,
  Text,
  Menu,
  Divider,
  useTheme,
  TextInput,
  Switch,
} from 'react-native-paper';
import {
  RecipeListParams,
  BrewingMethod,
  RecipeDifficulty,
} from '@coffeeplexity/shared';
import { useDebounce } from '@coffeeplexity/shared';
import { Filter, ChevronDown } from 'lucide-react-native';

interface RecipeSearchProps {
  onParamsChange: (params: RecipeListParams) => void;
  initialParams?: RecipeListParams;
}

export function RecipeSearch({
  onParamsChange,
  initialParams,
}: RecipeSearchProps) {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState(initialParams?.search || '');
  const [showFilters, setShowFilters] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);

  const [filters, setFilters] = useState({
    brewing_method: initialParams?.filters?.brewing_method || [],
    difficulty: initialParams?.filters?.difficulty || [],
    prep_time_max: initialParams?.filters?.prep_time_max || undefined,
    total_time_max: initialParams?.filters?.total_time_max || undefined,
    is_featured: initialParams?.filters?.is_featured || undefined,
  });

  const [sortBy, setSortBy] = useState(initialParams?.sort_by || 'created_at');
  const [sortOrder, setSortOrder] = useState(
    initialParams?.sort_order || 'desc'
  );

  const debouncedSearch = useDebounce(searchQuery, 300);

  const updateParams = useCallback(() => {
    const params: RecipeListParams = {
      search: debouncedSearch || undefined,
      filters: {
        brewing_method:
          filters.brewing_method.length > 0
            ? filters.brewing_method
            : undefined,
        difficulty:
          filters.difficulty.length > 0 ? filters.difficulty : undefined,
        prep_time_max: filters.prep_time_max,
        total_time_max: filters.total_time_max,
        is_featured: filters.is_featured,
      },
      sort_by: sortBy as any,
      sort_order: sortOrder as any,
    };

    // Remove empty filters
    if (
      Object.values(params.filters || {}).every(
        v => v === undefined || (Array.isArray(v) && v.length === 0)
      )
    ) {
      delete params.filters;
    }

    onParamsChange(params);
  }, [debouncedSearch, filters, sortBy, sortOrder, onParamsChange]);

  useEffect(() => {
    updateParams();
  }, [updateParams]);

  const handleBrewingMethodToggle = (method: BrewingMethod) => {
    setFilters(prev => ({
      ...prev,
      brewing_method: prev.brewing_method.includes(method)
        ? prev.brewing_method.filter(m => m !== method)
        : [...prev.brewing_method, method],
    }));
  };

  const handleDifficultyToggle = (difficulty: RecipeDifficulty) => {
    setFilters(prev => ({
      ...prev,
      difficulty: prev.difficulty.includes(difficulty)
        ? prev.difficulty.filter(d => d !== difficulty)
        : [...prev.difficulty, difficulty],
    }));
  };

  const clearFilters = () => {
    setFilters({
      brewing_method: [],
      difficulty: [],
      prep_time_max: undefined,
      total_time_max: undefined,
      is_featured: undefined,
    });
    setSearchQuery('');
    setSortBy('created_at');
    setSortOrder('desc');
  };

  const hasActiveFilters =
    searchQuery ||
    filters.brewing_method.length > 0 ||
    filters.difficulty.length > 0 ||
    filters.prep_time_max ||
    filters.total_time_max ||
    filters.is_featured !== undefined;

  const sortOptions = [
    { key: 'created_at-desc', label: 'Newest First' },
    { key: 'created_at-asc', label: 'Oldest First' },
    { key: 'rating-desc', label: 'Highest Rated' },
    { key: 'rating-asc', label: 'Lowest Rated' },
    { key: 'view_count-desc', label: 'Most Popular' },
    { key: 'favorite_count-desc', label: 'Most Liked' },
  ];

  const currentSortLabel =
    sortOptions.find(option => option.key === `${sortBy}-${sortOrder}`)
      ?.label || 'Newest First';

  return (
    <View style={styles.container}>
      {/* Search Bar */}
      <Searchbar
        placeholder='Search recipes...'
        onChangeText={setSearchQuery}
        value={searchQuery}
        style={[styles.searchBar, { backgroundColor: theme.colors.surface }]}
        iconColor={theme.colors.onSurfaceVariant}
      />

      {/* Filter and Sort Controls */}
      <View style={styles.controls}>
        <Button
          mode={showFilters || hasActiveFilters ? 'contained' : 'outlined'}
          onPress={() => setShowFilters(!showFilters)}
          icon={({ size, color }) => <Filter size={size} color={color} />}
          style={styles.filterButton}
          contentStyle={styles.buttonContent}
        >
          Filters {hasActiveFilters && '•'}
        </Button>

        <Menu
          visible={showSortMenu}
          onDismiss={() => setShowSortMenu(false)}
          anchor={
            <Button
              mode='outlined'
              onPress={() => setShowSortMenu(true)}
              icon={({ size, color }) => (
                <ChevronDown size={size} color={color} />
              )}
              contentStyle={styles.buttonContent}
            >
              {currentSortLabel}
            </Button>
          }
        >
          {sortOptions.map(option => (
            <Menu.Item
              key={option.key}
              onPress={() => {
                const [newSortBy, newSortOrder] = option.key.split('-');
                setSortBy(newSortBy);
                setSortOrder(newSortOrder);
                setShowSortMenu(false);
              }}
              title={option.label}
            />
          ))}
        </Menu>
      </View>

      {/* Filters Panel */}
      {showFilters && (
        <View
          style={[
            styles.filtersPanel,
            { backgroundColor: theme.colors.surfaceVariant },
          ]}
        >
          <View style={styles.filtersHeader}>
            <Text variant='titleMedium'>Filters</Text>
            {hasActiveFilters && (
              <Button mode='text' onPress={clearFilters}>
                Clear All
              </Button>
            )}
          </View>

          <ScrollView
            style={styles.filtersContent}
            showsVerticalScrollIndicator={false}
          >
            {/* Brewing Methods */}
            <View style={styles.filterSection}>
              <Text variant='titleSmall' style={styles.sectionTitle}>
                Brewing Method
              </Text>
              <View style={styles.chipContainer}>
                {Object.values(BrewingMethod).map(method => (
                  <Chip
                    key={method}
                    selected={filters.brewing_method.includes(method)}
                    onPress={() => handleBrewingMethodToggle(method)}
                    style={styles.chip}
                    textStyle={styles.chipText}
                  >
                    {method
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, l => l.toUpperCase())}
                  </Chip>
                ))}
              </View>
            </View>

            <Divider style={styles.divider} />

            {/* Difficulty */}
            <View style={styles.filterSection}>
              <Text variant='titleSmall' style={styles.sectionTitle}>
                Difficulty
              </Text>
              <View style={styles.chipContainer}>
                {Object.values(RecipeDifficulty).map(difficulty => (
                  <Chip
                    key={difficulty}
                    selected={filters.difficulty.includes(difficulty)}
                    onPress={() => handleDifficultyToggle(difficulty)}
                    style={styles.chip}
                    textStyle={styles.chipText}
                  >
                    {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                  </Chip>
                ))}
              </View>
            </View>

            <Divider style={styles.divider} />

            {/* Time Filters */}
            <View style={styles.filterSection}>
              <Text variant='titleSmall' style={styles.sectionTitle}>
                Time Limits
              </Text>
              <View style={styles.timeInputs}>
                <View style={styles.timeInput}>
                  <Text variant='bodySmall' style={styles.inputLabel}>
                    Max Prep Time (min)
                  </Text>
                  <TextInput
                    mode='outlined'
                    keyboardType='numeric'
                    value={filters.prep_time_max?.toString() || ''}
                    onChangeText={text =>
                      setFilters(prev => ({
                        ...prev,
                        prep_time_max: text ? parseInt(text) : undefined,
                      }))
                    }
                    placeholder='Any'
                    style={styles.textInput}
                    dense
                  />
                </View>
                <View style={styles.timeInput}>
                  <Text variant='bodySmall' style={styles.inputLabel}>
                    Max Total Time (min)
                  </Text>
                  <TextInput
                    mode='outlined'
                    keyboardType='numeric'
                    value={filters.total_time_max?.toString() || ''}
                    onChangeText={text =>
                      setFilters(prev => ({
                        ...prev,
                        total_time_max: text ? parseInt(text) : undefined,
                      }))
                    }
                    placeholder='Any'
                    style={styles.textInput}
                    dense
                  />
                </View>
              </View>
            </View>

            <Divider style={styles.divider} />

            {/* Featured Toggle */}
            <View style={styles.filterSection}>
              <View style={styles.switchRow}>
                <Text variant='bodyMedium'>Featured recipes only</Text>
                <Switch
                  value={filters.is_featured === true}
                  onValueChange={value =>
                    setFilters(prev => ({
                      ...prev,
                      is_featured: value ? true : undefined,
                    }))
                  }
                />
              </View>
            </View>
          </ScrollView>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  searchBar: {
    marginBottom: 6,
    elevation: 2,
  },
  controls: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  filterButton: {
    flex: 1,
    marginRight: 8,
  },
  buttonContent: {
    height: 40,
  },
  filtersPanel: {
    borderRadius: 12,
    marginTop: 8,
    maxHeight: 400,
  },
  filtersHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    paddingBottom: 8,
  },
  filtersContent: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  filterSection: {
    marginBottom: 16,
  },
  sectionTitle: {
    marginBottom: 8,
    fontWeight: '600',
  },
  chipContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    marginRight: 8,
    marginBottom: 8,
  },
  chipText: {
    fontSize: 12,
  },
  divider: {
    marginVertical: 8,
  },
  timeInputs: {
    flexDirection: 'row',
    gap: 12,
  },
  timeInput: {
    flex: 1,
  },
  inputLabel: {
    marginBottom: 4,
  },
  textInput: {
    height: 40,
  },
  switchRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});
